import { useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Check, CreditCard, Loader2, Plus, X } from "lucide-react";
import {
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	AccordionTrigger,
} from "@/components/Accordion";
import { AlertBar } from "@/components/AlertBar";
import { CopyableInput } from "@/components/CopyableInput";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { Description, Field, InfoLabel } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { SecretInput } from "@/components/SecretInput";
import { useHighlight } from "@/hooks/use-highlight";
import { orpc } from "@/orpc/client";
import type { IapConfigEntry, IapConfigsSection, IapProvider } from "../types";

/** Display names for each IAP provider. */
const PROVIDER_LABELS: Record<IapProvider, string> = {
	revenuecat: "RevenueCat",
	apple: "Apple (App Store Server Notifications)",
	google: "Google (Real-time Developer Notifications)",
};

const ALL_PROVIDERS: IapProvider[] = ["revenuecat", "apple", "google"];

interface IapProviderSectionProps {
	iapConfigs: IapConfigsSection;
	hasExistingSecret: (provider: string, field: string) => boolean;
}

export function IapProviderSection({
	iapConfigs,
	hasExistingSecret,
}: IapProviderSectionProps) {
	const { ref: highlightRef, isHighlighted } = useHighlight("iap");

	const configuredProviders = iapConfigs.value.map((c) => c.provider);
	const availableProviders = ALL_PROVIDERS.filter(
		(p) => !configuredProviders.includes(p),
	);

	function addProvider(provider: IapProvider) {
		const empty = getEmptyConfig(provider);
		iapConfigs.update([...iapConfigs.value, empty]);
	}

	function removeProvider(provider: IapProvider) {
		iapConfigs.update(iapConfigs.value.filter((c) => c.provider !== provider));
	}

	function updateConfig(updated: IapConfigEntry) {
		iapConfigs.update(
			iapConfigs.value.map((c) =>
				c.provider === updated.provider ? updated : c,
			),
		);
	}

	return (
		<div
			ref={highlightRef}
			className={isHighlighted ? "animate-highlight" : ""}
		>
			<AccordionItem value="iap">
				<AccordionHeader>
					<AccordionTrigger>
						<div className="flex items-center gap-3">
							<div className="flex size-8 items-center justify-center rounded-lg bg-warning/10 text-warning">
								<CreditCard className="size-4" />
							</div>
							<div className="flex flex-col items-start">
								<span className="font-semibold">In-App Purchase Providers</span>
								<span className="text-xs text-base-content/50 font-normal">
									Connect revenue tracking for partner earnings
								</span>
							</div>
						</div>
					</AccordionTrigger>
				</AccordionHeader>
				<AccordionPanel>
					<div className="flex flex-col gap-4 pt-2">
						<AlertBar variant="docs" className="mb-0 shadow-none">
							See the{" "}
							<a
								href="https://docs.biteasy.co/setup/end-to-end-integration/webhooks/"
								target="_blank"
								rel="noopener noreferrer"
								className="underline text-primary hover:text-primary/80"
							>
								setup guide
							</a>{" "}
							for integration instructions.
						</AlertBar>

						{/* Configured providers */}
						{iapConfigs.value.map((config) => (
							<ProviderCard
								key={config.provider}
								config={config}
								onUpdate={updateConfig}
								onRemove={() => removeProvider(config.provider)}
								hasExistingSecret={(field) =>
									hasExistingSecret(config.provider, field)
								}
								isSaving={iapConfigs.isSaving}
								isSaved={iapConfigs.isSaved}
							/>
						))}

						{/* Empty state */}
						{iapConfigs.value.length === 0 && (
							<p className="text-sm text-base-content/50">
								Add an IAP provider to track revenue from purchases and
								renewals.
							</p>
						)}

						{/* Add provider */}
						{availableProviders.length > 0 && (
							<AddProviderSelect
								available={availableProviders}
								onAdd={addProvider}
							/>
						)}
					</div>
				</AccordionPanel>
			</AccordionItem>
		</div>
	);
}

// ─── Provider Card ───────────────────────────────────────────────────────────

interface ProviderCardProps {
	config: IapConfigEntry;
	onUpdate: (config: IapConfigEntry) => void;
	onRemove: () => void;
	hasExistingSecret: (field: string) => boolean;
	isSaving: boolean;
	isSaved: boolean;
}

function ProviderCard({
	config,
	onUpdate,
	onRemove,
	hasExistingSecret,
	isSaving,
	isSaved,
}: ProviderCardProps) {
	return (
		<div className="rounded-lg border border-base-content/10 bg-base-200/20">
			{/* Card header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-base-content/5">
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium">
						{PROVIDER_LABELS[config.provider]}
					</span>
					{isSaving && (
						<Loader2 className="h-3.5 w-3.5 animate-spin text-base-content/40" />
					)}
					{isSaved && !isSaving && (
						<Check className="h-3.5 w-3.5 text-success" />
					)}
				</div>
				<button
					type="button"
					onClick={onRemove}
					className="rounded p-1 text-base-content/40 hover:text-error hover:bg-error/10 transition-colors"
					title={`Remove ${PROVIDER_LABELS[config.provider]}`}
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			{/* Card body */}
			<div className="flex flex-col gap-5 px-4 py-4">
				{config.provider === "revenuecat" && (
					<RevenueCatFields
						config={config}
						onUpdate={onUpdate}
						hasExistingSecret={hasExistingSecret}
					/>
				)}
				{config.provider === "apple" && (
					<AppleFields
						config={config}
						onUpdate={onUpdate}
						hasExistingSecret={hasExistingSecret}
					/>
				)}
				{config.provider === "google" && (
					<GoogleFields
						config={config}
						onUpdate={onUpdate}
						hasExistingSecret={hasExistingSecret}
					/>
				)}
			</div>
		</div>
	);
}

// ─── Add Provider ────────────────────────────────────────────────────────────

function AddProviderSelect({
	available,
	onAdd,
}: {
	available: IapProvider[];
	onAdd: (provider: IapProvider) => void;
}) {
	return (
		<Dropdown>
			<DropdownButton outline>
				<Plus className="h-4 w-4" data-slot="icon" />
				Add provider
			</DropdownButton>
			<DropdownMenu>
				{available.map((p) => (
					<DropdownItem key={p} onClick={() => onAdd(p)}>
						{PROVIDER_LABELS[p]}
					</DropdownItem>
				))}
			</DropdownMenu>
		</Dropdown>
	);
}

// ─── RevenueCat Fields ───────────────────────────────────────────────────────

function RevenueCatFields({
	config,
	onUpdate,
	hasExistingSecret,
}: {
	config: Extract<IapConfigEntry, { provider: "revenuecat" }>;
	onUpdate: (config: IapConfigEntry) => void;
	hasExistingSecret: (field: string) => boolean;
}) {
	const { appId } = useParams({ strict: false });
	const webhookUrl = `${import.meta.env.VITE_API_URL}/api/v1/revenuecat/${appId}`;

	return (
		<>
			<Field>
				<InfoLabel info="Configure this URL in your RevenueCat project's webhook settings. RevenueCat will send purchase events to this endpoint.">
					Webhook URL
				</InfoLabel>
				<CopyableInput value={webhookUrl} />
				<Description>
					Copy this URL and paste it in{" "}
					<a
						href="https://app.revenuecat.com"
						target="_blank"
						rel="noopener noreferrer"
						className="underline text-primary hover:text-primary/80"
					>
						RevenueCat Dashboard
					</a>{" "}
					→ Project Settings → Integrations → Webhooks
				</Description>
			</Field>

			<Field>
				<InfoLabel info="The Bearer token used to authenticate incoming webhooks from RevenueCat. Must match the Authorization header configured in RevenueCat.">
					Webhook Authorization Secret
				</InfoLabel>
				<SecretInput
					value={config.webhookAuthorizationSecret}
					onChange={(value) =>
						onUpdate({
							provider: "revenuecat",
							webhookAuthorizationSecret: value,
						})
					}
					hasExistingSecret={hasExistingSecret("webhookAuthorizationSecret")}
					placeholder="your-secret-token"
				/>
				{hasExistingSecret("webhookAuthorizationSecret") && (
					<Description className="text-xs text-base-content/60">
						Value is encrypted. Enter a new value to replace it.
					</Description>
				)}
			</Field>
		</>
	);
}

// ─── Apple Fields ────────────────────────────────────────────────────────────

function AppleFields({
	config,
	onUpdate,
	hasExistingSecret,
}: {
	config: Extract<IapConfigEntry, { provider: "apple" }>;
	onUpdate: (config: IapConfigEntry) => void;
	hasExistingSecret: (field: string) => boolean;
}) {
	const { appId } = useParams({ from: "/_app/apps/$appId/" });
	const { data: app } = useSuspenseQuery(
		orpc.apps.detail.queryOptions({ input: { id: appId } }),
	);

	const webhookUrl = `${import.meta.env.VITE_API_URL}/api/v1/apple/${appId}`;

	return (
		<>
			<Field>
				<InfoLabel info="Apple sends signed JWS notifications to this URL.">
					Webhook URL
				</InfoLabel>
				<CopyableInput value={webhookUrl} />
				<Description>
					Copy this URL and paste it in{" "}
					<a
						href="https://appstoreconnect.apple.com"
						target="_blank"
						rel="noopener noreferrer"
						className="underline text-primary hover:text-primary/80"
					>
						App Store Connect
					</a>{" "}
					→ Apps → {app.name} → App Information → App Store Server Notifications
					→ Production Server URL
				</Description>
			</Field>

			<div className="grid grid-cols-2 gap-4">
				<Field>
					<InfoLabel info="Your app's bundle identifier (e.g., com.example.app).">
						Bundle ID
					</InfoLabel>
					<Input
						value={config.bundleId}
						onChange={(e) => onUpdate({ ...config, bundleId: e.target.value })}
						placeholder="com.example.app"
					/>
				</Field>

				<Field>
					<InfoLabel info="Your app's numeric App Store ID.">
						App Apple ID
					</InfoLabel>
					<Input
						value={config.appAppleId}
						onChange={(e) =>
							onUpdate({ ...config, appAppleId: e.target.value })
						}
						placeholder="123456789"
					/>
				</Field>

				<Field>
					<InfoLabel info="App Store Connect API Issuer ID.">
						Issuer ID
					</InfoLabel>
					<Input
						value={config.issuerId}
						onChange={(e) => onUpdate({ ...config, issuerId: e.target.value })}
						placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
					/>
				</Field>

				<Field>
					<InfoLabel info="The Key ID for your App Store Connect API key.">
						Key ID
					</InfoLabel>
					<Input
						value={config.keyId}
						onChange={(e) => onUpdate({ ...config, keyId: e.target.value })}
						placeholder="XXXXXXXXXX"
					/>
				</Field>
			</div>

			<Field>
				<InfoLabel info="Base64-encoded contents of the .p8 private key file.">
					Private Key (Base64)
				</InfoLabel>
				<SecretInput
					value={config.encodedKey}
					onChange={(value) => onUpdate({ ...config, encodedKey: value })}
					hasExistingSecret={hasExistingSecret("encodedKey")}
					placeholder="LS0tLS1CRUdJTi..."
				/>
				{hasExistingSecret("encodedKey") && (
					<Description className="text-xs text-base-content/60">
						Value is encrypted. Enter a new value to replace it.
					</Description>
				)}
			</Field>
		</>
	);
}

// ─── Google Fields ───────────────────────────────────────────────────────────

function GoogleFields({
	config,
	onUpdate,
	hasExistingSecret,
}: {
	config: Extract<IapConfigEntry, { provider: "google" }>;
	onUpdate: (config: IapConfigEntry) => void;
	hasExistingSecret: (field: string) => boolean;
}) {
	const { appId } = useParams({ strict: false });
	const webhookUrl = `${import.meta.env.VITE_API_URL}/api/v1/google/${appId}`;

	return (
		<>
			<Field>
				<InfoLabel info="Configure a Pub/Sub push subscription to send notifications to this URL.">
					Webhook URL
				</InfoLabel>
				<CopyableInput value={webhookUrl} />
				<Description>
					Create a push subscription in{" "}
					<a
						href="https://console.cloud.google.com/cloudpubsub"
						target="_blank"
						rel="noopener noreferrer"
						className="underline text-primary hover:text-primary/80"
					>
						Google Cloud Console
					</a>{" "}
					→ Pub/Sub → Subscriptions → Create subscription → set Delivery type to
					Push and paste this URL as the endpoint.
				</Description>
			</Field>

			<div className="grid grid-cols-2 gap-4">
				<Field>
					<InfoLabel info="Your Android app's package name (e.g., com.example.app).">
						Package Name
					</InfoLabel>
					<Input
						value={config.packageName}
						onChange={(e) =>
							onUpdate({ ...config, packageName: e.target.value })
						}
						placeholder="com.example.app"
					/>
				</Field>

				<Field>
					<InfoLabel info="Google Cloud Pub/Sub topic for real-time notifications.">
						Pub/Sub Topic
					</InfoLabel>
					<Input
						value={config.pubsubTopic}
						onChange={(e) =>
							onUpdate({ ...config, pubsubTopic: e.target.value })
						}
						placeholder="projects/my-project/topics/play-rtdn"
					/>
				</Field>
			</div>

			<Field>
				<InfoLabel info="The full JSON key for a Google Cloud service account with access to the Google Play Developer API. Used to verify purchases and fetch pricing.">
					Service Account JSON
				</InfoLabel>
				<SecretInput
					value={config.serviceAccountJson}
					onChange={(value) =>
						onUpdate({ ...config, serviceAccountJson: value })
					}
					hasExistingSecret={hasExistingSecret("serviceAccountJson")}
					placeholder='{"type": "service_account", ...}'
				/>
				{hasExistingSecret("serviceAccountJson") && (
					<Description className="text-xs text-base-content/60">
						Value is encrypted. Enter a new value to replace it.
					</Description>
				)}
			</Field>
		</>
	);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getEmptyConfig(provider: IapProvider): IapConfigEntry {
	switch (provider) {
		case "revenuecat":
			return { provider: "revenuecat", webhookAuthorizationSecret: "" };
		case "apple":
			return {
				provider: "apple",
				bundleId: "",
				appAppleId: "",
				issuerId: "",
				keyId: "",
				encodedKey: "",
			};
		case "google":
			return {
				provider: "google",
				packageName: "",
				pubsubTopic: "",
				serviceAccountJson: "",
			};
	}
}
